import { useEffect } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";

import { DemoBadge } from "@/components/DemoBadge";
import { SyncButton } from "@/components/SyncButton";
import { useAuth } from "@/context/AuthContext";
import { useChromeHeader } from "@/context/ChromeContext";
import { useTaskEditor } from "@/context/TaskEditorContext";
import { useWorkBlockAutoRun } from "@/hooks/useWorkBlockAutoRun";
import { useAutoSync, type AutoSyncState } from "@/hooks/useAutoSync";
import { appMode } from "@/lib/pocketbase";
import { Settings } from "@/views/Settings";

import { LogSheetProvider } from "./components/LogSheetContext";
import { TimerBar } from "./components/TimerBar";
import { MobileTabBar } from "./MobileTabBar";
import { MobileProject } from "./screens/MobileProject";
import { MobileProjects } from "./screens/MobileProjects";
import { MobileEntries } from "./screens/MobileEntries";
import { MobileInsights } from "./screens/MobileInsights";
import { MobileToday } from "./screens/MobileToday";
import { NotOnPhone } from "./screens/NotOnPhone";

/**
 * The phone shell — its own header, its own tab bar, its own route tree.
 *
 * Deliberately separate from the desktop Shell rather than another branch
 * inside it. The old approach put 68 `mobile ?` conditionals across 13 files,
 * and the result read as a shrunken desktop app because that is exactly what it
 * was.
 *
 * FOUR DESTINATIONS: Time, Tasks, Entries, Insights (Tasks is the /projects
 * route). An earlier pass folded these into two
 * buckets with segments inside them; the tracker's arrangement is what a year
 * of real use settled on, and mirroring it means muscle memory carries over
 * rather than having to be retrained.
 *
 * Height is 100dvh, not 100vh: on iOS Safari the toolbar collapses as you
 * scroll, and 100vh measures the *expanded* viewport, which pushes the tab bar
 * below the fold until the toolbar happens to retract.
 */
export function MobileShell() {
  // The mirror only refreshes while a browser has the app open, and until now
  // the only caller was the desktop Sidebar — so the phone showed whatever was
  // in PocketBase the last time a laptop was open. A task added in Todoist this
  // morning would not appear here until you sat down at a desk, which rather
  // defeats carrying it around. Syncs on load and on returning to the tab,
  // throttled to five minutes.
  const sync = useAutoSync();
  // And the coming week's work blocks, once your planning time has passed.
  useWorkBlockAutoRun();

  // No pinch-zoom: an app's screen stays put. iOS ignores user-scalable=no in
  // the viewport tag, but its own gesture events can still be cancelled. Only
  // while this shell is mounted, so the desktop layout keeps browser zoom.
  useEffect(() => {
    const stop = (e: Event) => e.preventDefault();
    const events = ["gesturestart", "gesturechange", "gestureend"];
    for (const name of events) document.addEventListener(name, stop, { passive: false });
    return () => { for (const name of events) document.removeEventListener(name, stop); };
  }, []);

  return (
    <LogSheetProvider>
      <div
        className="mobile-shell"
        style={{
          display: "flex", flexDirection: "column",
          // --bg, not --surface: the cards ARE --surface, and painting the
          // frame the same white left them sitting on themselves with only a
          // hairline between. The page has to be the off-white for a card to
          // read as lifted off it.
          overflow: "hidden", background: "var(--bg)",
        }}
      >
        <MobileHeader sync={sync} />

        <main style={{ flex: 1, minWidth: 0, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
          <Routes>
            <Route path="/" element={<MobileToday />} />
            <Route path="/entries" element={<MobileEntries />} />
            <Route path="/insights" element={<MobileInsights />} />
            <Route path="/projects" element={<MobileProjects />} />
            {/* The desktop keeps Today and Entries under one Time route; on the
                phone they are tabs of their own, so /time lands on Today. */}
            <Route path="/time" element={<MobileToday />} />
            <Route path="/reports" element={<MobileInsights />} />
            <Route path="/project/:id" element={<MobileProject />} />
            <Route path="/settings" element={<Settings />} />
            {/* Search is a segment of Projects now; the old path opens it there. */}
            <Route path="/search" element={<MobileProjects initialSegment="search" />} />
            {/* Desk work. Reachable by link or old bookmark, so they explain
                themselves rather than 404-ing or rendering unusably. */}
            <Route path="/calendar" element={<NotOnPhone name="The calendar" />} />
            <Route path="/roadmap" element={<NotOnPhone name="The roadmap" />} />
            <Route path="/planner" element={<NotOnPhone name="The planner" />} />
            <Route path="/board" element={<NotOnPhone name="The status board" />} />
            <Route path="/import" element={<NotOnPhone name="Importing a plan" />} />
            {/* The phone has the Reimburse app for this, with the camera. */}
            <Route path="/expenses/*" element={<NotOnPhone name="Expenses" detail="On your phone, use the Reimburse app — it works on the same reports." />} />
            {/* The task lists that used to have tabs of their own are the
                Tasks segment of Projects now. Old links land there. */}
            <Route path="/today" element={<Navigate to="/projects" replace />} />
            <Route path="/overdue" element={<Navigate to="/projects" replace />} />
            <Route path="/upcoming" element={<Navigate to="/projects" replace />} />
            <Route path="/week" element={<Navigate to="/projects" replace />} />
            <Route path="/next" element={<Navigate to="/projects" replace />} />
            <Route path="/all" element={<Navigate to="/projects" replace />} />
            <Route path="*" element={<MobileToday />} />
          </Routes>
        </main>

        <TimerBar />
        <MobileTabBar />
        {appMode === "demo" && <DemoBadge />}
      </div>
    </LogSheetProvider>
  );
}

/**
 * One row: back, title, account. The desktop header stacks title, a search
 * field and a tab strip — three rows before any content on a 375pt screen.
 * The tab strip belongs to whichever screen needs it, as a segmented control.
 */
function MobileHeader({ sync }: { sync: AutoSyncState }) {
  const { title, subtitle, back } = useChromeHeader();
  const { quickAddHere } = useTaskEditor();
  const navigate = useNavigate();
  const onSettings = useLocation().pathname === "/settings";
  const { user } = useAuth();
  const initial = String((user as { name?: string; email?: string } | null)?.name
    || (user as { email?: string } | null)?.email || "?").charAt(0).toUpperCase();

  return (
    <header
      className="no-print"
      style={{
        flex: "none", display: "flex", alignItems: "center", gap: 10,
        padding: "calc(var(--safe-t) + 14px) 14px 10px",
        borderBottom: "1px solid var(--line)", background: "var(--surface)",
      }}
    >
      {(back || onSettings) && (
        <button
          onClick={() => navigate(-1)}
          aria-label="Back"
          style={{
            flex: "none", width: 36, height: 36, borderRadius: "50%", border: "none",
            background: "var(--tint)", color: "var(--ink2)", fontSize: 17, cursor: "pointer",
          }}
        >‹</button>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 19, fontWeight: 600, color: "var(--ink)",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>{title || "Project Hub"}</div>
        {subtitle && (
          <div style={{
            fontSize: 12, color: "var(--muted)",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>{subtitle}</div>
        )}
      </div>
      {/* How stale is this? The app is a projection of Todoist, so that is a
          question worth being able to answer — and act on — without going
          through Settings. Hidden in demo, where there is nothing to sync. */}
      <SyncButton state={sync} compact variant="icon" />

      <button
        onClick={quickAddHere}
        aria-label="Quick add a task"
        style={{
          flex: "none", width: 36, height: 36, borderRadius: "50%", border: "none",
          background: "var(--accent)", color: "var(--onAccent)", fontSize: 20, lineHeight: 1, cursor: "pointer",
        }}
      >+</button>

      {/* The avatar is the way into Settings, and Settings is the only screen
          reachable from anywhere — so on it, the same corner becomes the way
          out rather than a button that appears to do nothing. */}
      <button
        onClick={() => { if (onSettings) navigate(-1); else navigate("/settings"); }}
        aria-label={onSettings ? "Close settings" : "Settings"}
        style={{
          flex: "none", minWidth: 36, height: 36, padding: onSettings ? "0 12px" : 0,
          borderRadius: onSettings ? 999 : "50%", border: "none",
          background: "var(--accentTint)", color: "var(--accent)",
          fontSize: 14, fontWeight: 500, cursor: "pointer",
        }}
      >{onSettings ? "Done" : initial}</button>
    </header>
  );
}
